/**
 * Viewport background presets.
 *
 * The live viewport is filled with CSS behind the transparent 3D canvas.
 * Export and thumbnail paths can draw the same preset onto a 2D canvas before
 * compositing the WebGL frame.
 */

type ViewportBgFill =
  | {
      kind: "solid";
      color: string;
    }
  | {
      kind: "linear";
      colors: readonly string[];
    };

export type ViewportBgLiveEffect = {
  kind: "starryNight";
};

export interface ViewportBgPreset {
  label: string;
  fill: ViewportBgFill;
  liveEffect?: ViewportBgLiveEffect;
  /** Representative solid color for APIs that only accept flat colors. */
  color: string;
}

export const VIEWPORT_BG_PRESETS: Record<string, ViewportBgPreset> = {
  default: {
    label: "Default",
    fill: { kind: "solid", color: "#464646" },
    color: "#464646",
  },
  amber: {
    label: "Amber",
    fill: { kind: "solid", color: "#4a3828" },
    color: "#4a3828",
  },
  lightGrey: {
    label: "Light Grey",
    fill: { kind: "solid", color: "#505055" },
    color: "#505055",
  },
  deepBlue: {
    label: "Deep Blue",
    fill: { kind: "solid", color: "#1a2540" },
    color: "#1a2540",
  },
  candySky: {
    label: "Candy Sky",
    fill: { kind: "linear", colors: ["#4a7590", "#904680", "#91714c"] },
    color: "#794c75",
  },
  citrusGlow: {
    label: "Citrus Glow",
    fill: { kind: "linear", colors: ["#80914c", "#91714c"] },
    color: "#887f4c",
  },
  aurora: {
    label: "Aurora",
    fill: { kind: "linear", colors: ["#4c7391", "#844c91"] },
    color: "#686091",
  },
  starryNight: {
    label: "Starry Night",
    fill: { kind: "linear", colors: ["#030613", "#071838", "#102a58"] },
    liveEffect: { kind: "starryNight" },
    color: "#071838",
  },
};

export const VIEWPORT_BG_PRESET_KEYS = Object.keys(VIEWPORT_BG_PRESETS);

export function resolveViewportBgPreset(key: string): ViewportBgPreset {
  return VIEWPORT_BG_PRESETS[key] ?? VIEWPORT_BG_PRESETS.default;
}

export function viewportBgCssBackground(preset: ViewportBgPreset): string {
  if (preset.fill.kind === "solid") return preset.fill.color;
  return `linear-gradient(to bottom, ${preset.fill.colors.join(", ")})`;
}

export function drawViewportBgPreset(
  ctx: CanvasRenderingContext2D,
  presetKey: string,
  width: number,
  height: number,
): void {
  const preset = resolveViewportBgPreset(presetKey);
  if (preset.fill.kind === "solid") {
    ctx.fillStyle = preset.fill.color;
  } else {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    const last = Math.max(1, preset.fill.colors.length - 1);
    preset.fill.colors.forEach((color, index) => {
      gradient.addColorStop(index / last, color);
    });
    ctx.fillStyle = gradient;
  }
  ctx.fillRect(0, 0, width, height);
}
